import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check,
  Copy,
  Link2,
  Loader2,
  LogOut,
  MessageCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import {
  SettingsContentLoading,
  SettingsLayout,
} from '../components/settings-layout';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { HTTPStatusError } from '../lib/chat-api';
import {
  createTelegramDMClaimCode,
  deleteTelegramDMLink,
  getTelegramDMState,
  startTelegramConnect,
  type TelegramDMClaimCode,
  type TelegramDMLink,
  type TelegramRuntimeStatus,
} from '../lib/telegram';

const telegramConnectMessageType = 'cueboard-telegram-connect-result';

type DMSetupState = 'Not Connected' | 'Waiting for Link' | 'Connected';

function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof HTTPStatusError) {
    return error.status;
  }
  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number'
  ) {
    return error.status;
  }
  return undefined;
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return 'Unknown';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function currentPathWithSearch() {
  return `${window.location.pathname}${window.location.search}`;
}

function dmSetupState(
  link: TelegramDMLink | null,
  code: TelegramDMClaimCode | null
): DMSetupState {
  if (link) {
    return 'Connected';
  }
  if (code) {
    return 'Waiting for Link';
  }
  return 'Not Connected';
}

export function TelegramSettingsPage() {
  const navigate = useNavigate();

  const [runtime, setRuntime] = useState<TelegramRuntimeStatus | null>(null);
  const [dmLink, setDMClaimLink] = useState<TelegramDMLink | null>(null);
  const [dmClaimCode, setDMClaimCode] = useState<TelegramDMClaimCode | null>(
    null
  );
  const [officialLoginEnabled, setOfficialLoginEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showManualFallback, setShowManualFallback] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState<string>('');
  const [error, setError] = useState<string>('');
  const connectPopupRef = useRef<Window | null>(null);
  // Snapshot of the DM link signature captured at the moment the user starts a
  // connect attempt. The polling effect uses this to detect "the link actually
  // changed because of THIS attempt" — without it, a user who is already
  // linked and clicks Connect (to re-link / switch accounts) would see the
  // pre-existing link in the first poll and prematurely fire success.
  // null means "no prior link existed when this attempt started".
  const connectStartLinkSignatureRef = useRef<string | null>(null);

  const applyTelegramState = useCallback(
    (state: Awaited<ReturnType<typeof getTelegramDMState>>) => {
      setRuntime(state.runtime ?? null);
      setDMClaimLink(state.dm_link ?? null);
      setDMClaimCode(state.dm_link_code ?? null);
      setOfficialLoginEnabled(Boolean(state.official_login_enabled));
      if (!state.official_login_enabled || state.dm_link_code) {
        setShowManualFallback(true);
      } else if (state.dm_link) {
        setShowManualFallback(false);
      }
    },
    []
  );

  const loadPage = useCallback(async () => {
    const state = await getTelegramDMState();
    applyTelegramState(state);
    setError('');
    return state;
  }, [applyTelegramState]);

  const closeConnectPopup = useCallback(() => {
    const popup = connectPopupRef.current;
    connectPopupRef.current = null;
    if (!popup) {
      return;
    }
    try {
      if (!popup.closed) {
        popup.close();
      }
    } catch {
      // Some browsers can throw if the popup navigated cross-origin.
    }
  }, []);

  const finishTelegramConnectSuccess = useCallback(
    (message = 'Telegram connected.') => {
      closeConnectPopup();
      setIsConnecting(false);
      connectStartLinkSignatureRef.current = null;
      setNotice(message);
      setError('');
      setShowManualFallback(false);
    },
    [closeConnectPopup]
  );

  // Stable identifier for a DM link snapshot. Combines telegram_user_id and
  // updated_at so re-linking the same Telegram account (updated_at bumps) is
  // detected just like switching to a new account (telegram_user_id changes).
  const dmLinkSignature = useCallback((link: TelegramDMLink | null) => {
    if (!link) {
      return '';
    }
    return `${link.telegram_user_id}|${link.updated_at}`;
  }, []);

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        await loadPage();
      } catch (err) {
        if (!active) {
          return;
        }
        if (getErrorStatus(err) === 401) {
          navigate(
            `/login?next=${encodeURIComponent(currentPathWithSearch())}`,
            { replace: true }
          );
          return;
        }
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load Telegram settings'
        );
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };
    void run();
    return () => {
      active = false;
    };
  }, [loadPage, navigate]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (connectPopupRef.current && event.source !== connectPopupRef.current) {
        return;
      }
      if (typeof event.data !== 'object' || event.data === null) {
        return;
      }
      const data = event.data as {
        type?: unknown;
        ok?: unknown;
        message?: unknown;
      };
      if (data.type !== telegramConnectMessageType) {
        return;
      }

      const message = typeof data.message === 'string' ? data.message : '';
      if (data.ok) {
        finishTelegramConnectSuccess(message || 'Telegram connected.');
        void loadPage();
        return;
      }

      closeConnectPopup();
      setIsConnecting(false);
      connectStartLinkSignatureRef.current = null;
      setNotice('');
      setError(message || 'Telegram connection failed.');
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [closeConnectPopup, finishTelegramConnectSuccess, loadPage]);

  useEffect(() => {
    if (!isConnecting) {
      return;
    }

    let active = true;
    let refreshInFlight = false;

    const refreshConnectState = async (options?: {
      failWhenUnlinked?: boolean;
    }) => {
      if (refreshInFlight) {
        return;
      }
      refreshInFlight = true;
      try {
        const state = await getTelegramDMState();
        if (!active) {
          return;
        }
        applyTelegramState(state);
        // Only treat as success when the link visible to this poll did not
        // already exist when the user pressed Connect. Re-link of the same
        // account bumps `updated_at`, so the composite signature changes; a
        // brand-new connection (no prior link) trivially passes the check.
        const currentSignature = dmLinkSignature(state.dm_link ?? null);
        const baselineSignature = connectStartLinkSignatureRef.current ?? '';
        if (
          state.dm_link &&
          currentSignature !== '' &&
          currentSignature !== baselineSignature
        ) {
          finishTelegramConnectSuccess('Telegram connected.');
          return;
        }
        if (options?.failWhenUnlinked) {
          setIsConnecting(false);
          connectStartLinkSignatureRef.current = null;
          setNotice('');
          setError('Telegram connection was cancelled before it completed.');
        }
      } catch (err) {
        if (!active || !options?.failWhenUnlinked) {
          return;
        }
        setIsConnecting(false);
        connectStartLinkSignatureRef.current = null;
        setNotice('');
        setError(
          err instanceof Error
            ? err.message
            : 'Telegram connection was cancelled before it completed.'
        );
      } finally {
        refreshInFlight = false;
      }
    };

    void refreshConnectState();

    const statePoll = window.setInterval(() => {
      void refreshConnectState();
    }, 2000);

    const closePoll = window.setInterval(() => {
      if (connectPopupRef.current?.closed !== true) {
        return;
      }
      if (refreshInFlight) {
        return;
      }
      connectPopupRef.current = null;
      void refreshConnectState({ failWhenUnlinked: true });
    }, 500);

    return () => {
      active = false;
      window.clearInterval(statePoll);
      window.clearInterval(closePoll);
    };
  }, [
    applyTelegramState,
    dmLinkSignature,
    finishTelegramConnectSuccess,
    isConnecting,
  ]);

  const handleConnectTelegram = useCallback(async () => {
    if (!officialLoginEnabled) {
      setShowManualFallback(true);
      return;
    }

    const width = 540;
    const height = 720;
    const left = Math.max(window.screenX + (window.outerWidth - width) / 2, 0);
    const top = Math.max(window.screenY + (window.outerHeight - height) / 2, 0);
    const popup = window.open(
      'about:blank',
      'cueboard-telegram-connect',
      `width=${width},height=${height},left=${left},top=${top},popup=yes`
    );
    if (!popup) {
      setError('Please allow popups to connect Telegram.');
      return;
    }

    connectPopupRef.current = popup;
    // Capture the current link signature so the polling effect can tell
    // "this is the link we just made" apart from "this is the link that was
    // already here when the user clicked Connect to re-link/switch accounts".
    connectStartLinkSignatureRef.current = dmLinkSignature(dmLink);
    setIsConnecting(true);
    setNotice('');
    setError('');

    try {
      const result = await startTelegramConnect();
      popup.location.href = result.authorization_url;
      popup.focus();
    } catch (err) {
      connectPopupRef.current = null;
      connectStartLinkSignatureRef.current = null;
      popup.close();
      setIsConnecting(false);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to start Telegram connection'
      );
    }
  }, [dmLink, dmLinkSignature, officialLoginEnabled]);

  const handleGenerateCode = useCallback(async () => {
    setIsGenerating(true);
    try {
      const code = await createTelegramDMClaimCode();
      setDMClaimCode(code);
      setError('');
      setNotice('One-time Telegram code ready.');
      setShowManualFallback(true);
      setCopied(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to create Telegram link code'
      );
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    setIsDisconnecting(true);
    try {
      await deleteTelegramDMLink();
      await loadPage();
      setNotice('Telegram DM disconnected.');
      setShowManualFallback(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to disconnect Telegram DM'
      );
    } finally {
      setIsDisconnecting(false);
    }
  }, [loadPage]);

  const handleCopyCommand = useCallback(async () => {
    if (!dmClaimCode?.code) {
      return;
    }
    const command = `/link ${dmClaimCode.code}`;
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setNotice('Telegram command copied.');
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Failed to copy Telegram command');
    }
  }, [dmClaimCode]);

  const setupState = dmSetupState(dmLink, dmClaimCode);
  const canUseOfficialConnect =
    officialLoginEnabled && runtime?.configured !== false;

  return (
    <SettingsLayout
      current="telegram"
      notice={notice}
      error={error}
      onNoticeDismiss={() => setNotice('')}
      onErrorDismiss={() => setError('')}
    >
      {isLoading ? (
        <SettingsContentLoading label="Loading Telegram settings..." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="size-5" />
                Connect with Telegram
              </CardTitle>
              <CardDescription>
                Start from Bridge, then keep using the same private Telegram
                chat.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  {
                    title: 'Step 1',
                    body: 'Click Connect Telegram from this page.',
                  },
                  {
                    title: 'Step 2',
                    body: 'Confirm in Telegram and make sure the bot can message you.',
                  },
                  {
                    title: 'Step 3',
                    body: 'Return here once the popup closes and your DM shows as connected.',
                  },
                ].map(item => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-border/70 bg-background/70 p-4"
                  >
                    <div className="text-sm font-medium text-muted-foreground">
                      {item.title}
                    </div>
                    <div className="mt-2 text-sm">{item.body}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  onClick={handleConnectTelegram}
                  disabled={isConnecting || !canUseOfficialConnect}
                >
                  {isConnecting ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Link2 className="mr-2 size-4" />
                  )}
                  Connect Telegram
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowManualFallback(value => !value)}
                >
                  Use fallback code
                </Button>
              </div>

              {!officialLoginEnabled ? (
                <div className="rounded-lg border border-dashed border-border/80 bg-background/50 p-4 text-sm text-muted-foreground">
                  Connecting from Bridge is not available here yet. You can
                  still use a fallback code below.
                </div>
              ) : null}

              {showManualFallback || !officialLoginEnabled ? (
                <div className="space-y-4 rounded-lg border border-border/70 bg-background/70 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="text-sm font-medium">Fallback code</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Generate a fallback code, then send{' '}
                        <span className="font-mono">/link &lt;code&gt;</span> to
                        the Telegram bot.
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGenerateCode}
                      disabled={isGenerating}
                    >
                      {isGenerating ? (
                        <Loader2 className="mr-2 size-4 animate-spin" />
                      ) : (
                        <Link2 className="mr-2 size-4" />
                      )}
                      {dmClaimCode ? 'Generate new code' : 'Generate code'}
                    </Button>
                  </div>

                  {dmClaimCode ? (
                    <div className="rounded-lg border border-border/70 bg-card p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium">
                            Current link code
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Expires {formatDateTime(dmClaimCode.expires_at)}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCopyCommand}
                        >
                          {copied ? (
                            <Check className="mr-2 size-4" />
                          ) : (
                            <Copy className="mr-2 size-4" />
                          )}
                          {copied ? 'Copied' : 'Copy command'}
                        </Button>
                      </div>
                      <div className="rounded-xl border border-border/70 bg-background p-3 font-mono text-sm">
                        /link {dmClaimCode.code}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border/80 bg-background/50 p-4 text-sm text-muted-foreground">
                      No active link code yet.
                    </div>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="size-5" />
                Current DM link
              </CardTitle>
              <CardDescription>
                Status for the Telegram account currently connected.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div
                className={cn(
                  'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium',
                  setupState === 'Connected'
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                    : setupState === 'Waiting for Link'
                      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                      : 'bg-muted text-muted-foreground'
                )}
              >
                {setupState}
              </div>

              {dmLink ? (
                <div className="rounded-lg border border-border/70 bg-background/70 p-4 text-sm">
                  <div className="font-medium">Telegram account</div>
                  <div className="mt-1 text-muted-foreground">
                    {dmLink.telegram_username?.trim()
                      ? `@${dmLink.telegram_username} (${dmLink.telegram_user_id})`
                      : dmLink.telegram_user_id}
                  </div>
                  <div className="mt-4 grid gap-3 text-muted-foreground sm:grid-cols-2">
                    <div>
                      <div className="text-xs uppercase tracking-wide">
                        Linked at
                      </div>
                      <div className="mt-1 text-sm text-foreground">
                        {formatDateTime(dmLink.created_at)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide">
                        Last updated
                      </div>
                      <div className="mt-1 text-sm text-foreground">
                        {formatDateTime(dmLink.updated_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-border/80 bg-background/50 p-6 text-sm text-muted-foreground">
                  No Telegram chat is connected yet.
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDisconnect}
                  disabled={!dmLink || isDisconnecting}
                >
                  {isDisconnecting ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <LogOut className="mr-2 size-4" />
                  )}
                  Disconnect DM
                </Button>
                {!showManualFallback ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowManualFallback(true)}
                    disabled={isGenerating}
                  >
                    Use fallback code
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </SettingsLayout>
  );
}
